/**
 * The MFE bundle-LOAD lifecycle: the states a nested-MFE mount can be in, and the copy its fallback renders.
 *
 * WHY THE STATES ARE A DECISION AND NOT A COMPONENT DETAIL. `useMFEContainerMount` appends one `<script type="module">`
 * and then waits for the bundle to publish `window.__federaMFE_<page>`. Until 2026-08-20 nothing observed the two ways
 * that wait can end badly -- the script 404s, or it loads and never publishes -- so a broken bundle rendered an EMPTY
 * DIV forever: no spinner, no message, no retry, and nothing in the DOM a reader could tell apart from "this section is
 * legitimately blank". The hook owns the transitions ({@link MFE_MOUNT_STATUS}) and the frame renders them, so the
 * vocabulary sits in neither.
 *
 * `failed` IS RECOVERABLE, which is why the copy offers a retry rather than an apology: the overwhelmingly common cause
 * is a transient network failure against a hashed asset URL, and re-appending the script is the whole cure. The retry
 * VERB is not respelled here -- `ERROR_ACTION_LABELS.tryAgain` (`components/error/errorLabels.ts`) already owns it for
 * every error surface, and the frame reads it from there.
 */

import { couldNotBeLoaded } from '@federa/const/read-failure-copy';

/** The states of one bundle load. `ready` means the bundle PUBLISHED its global, not merely that the script tag resolved.
 *  `outdated` is a ready mount whose MFE has a newer build live since this page loaded it (`useMfeEntryUpdate`): it
 *  still works, and only a reload -- the person's call, never the page's -- runs the new one. */
export const MFE_MOUNT_STATUS = {
	loading: 'loading',
	ready: 'ready',
	failed: 'failed',
	outdated: 'outdated',
} as const;

/**
 * The fallback's words.
 *
 * `failedTitle` names the PANEL rather than the page on purpose: a nested MFE occupies one tab of a working surface, and
 * a viewer told "something went wrong" reads it as the whole app being down and leaves. `failedBody` states what still
 * works, what does not, and the one action available, in that order.
 */
export const MFE_MOUNT_COPY = {
	loadingLabel: 'Loading this section...',
	failedTitle: couldNotBeLoaded('This section'),
	failedBody: 'The rest of the page still works -- only this panel failed to load. Check your connection and try again.',
	/** The RENDER-time failure, distinct from the LOAD-time one: the bundle arrived and mounted, then threw. Retrying the
	 *  script fetch cannot help, so the boundary offers a remount instead and says so. */
	crashedTitle: 'This section stopped responding',
	crashedBody: 'Something in this panel failed while it was running. Reloading it usually clears the problem.',
	/** A newer build of the section is live. The body says what a reload costs, so a person mid-call waits for the end
	 *  of it: the page never reloads on its own. */
	outdatedTitle: 'A new version of this section is ready',
	outdatedBody: 'Reload when it suits you. Reloading restarts the page, so finish what you are doing first.',
	reloadLabel: 'Reload page',
} as const;
